import type { ComponentInfo } from "./types"

interface FileSourceInfo {
  fileName?: string
}

type ReactComponentFunction = Function & {
  displayName?: string
  __source?: FileSourceInfo
}

interface ReactComponentObject {
  $$typeof?: symbol
  render?: ReactComponentLike
  type?: ReactComponentLike
  displayName?: string
  name?: string
  __source?: FileSourceInfo
}

type ReactComponentLike =
  | ReactComponentFunction
  | ReactComponentObject
  | string
  | null
  | undefined

interface ReactFiberNode {
  return?: ReactFiberNode | null
  elementType?: ReactComponentLike
  type?: ReactComponentLike
  memoizedProps?: unknown
  memoizedState?: unknown
  _debugSource?: FileSourceInfo
  _source?: FileSourceInfo
  _debugOwner?: { _debugSource?: FileSourceInfo }
}

declare global {
  interface Window {
    __clickToComponentWorkspaceRoot?: string
  }
}

/**
 * Finds the React fiber node associated with a DOM element
 */
export function findReactFiber(element: HTMLElement): ReactFiberNode | null {
  // React 16+ stores fiber nodes with keys like __reactFiber$randomkey
  // React 15 and below use __reactInternalInstance$randomkey
  const entries = Object.entries(
    element as unknown as Record<string, ReactFiberNode>
  )

  for (const [key, fiber] of entries) {
    if (
      key.startsWith("__reactFiber$") ||
      key.startsWith("__reactInternalInstance$")
    ) {
      return fiber
    }
  }

  // If not found, try parent element
  if (element.parentElement) {
    return findReactFiber(element.parentElement)
  }

  return null
}

/**
 * Gets the component name from a React component type
 */
export function getComponentName(component: ReactComponentLike): string | null {
  if (!component) return null

  // Function component
  if (typeof component === "function") {
    return component.displayName || component.name || "Anonymous"
  }

  const componentObject = component as ReactComponentObject

  // Forward ref component
  if (componentObject.$$typeof && componentObject.render) {
    const render = componentObject.render as
      | ReactComponentFunction
      | ReactComponentObject
    return render.displayName || render.name || "ForwardRef"
  }

  // Memo component
  if (componentObject.$$typeof && componentObject.type) {
    return getComponentName(componentObject.type)
  }

  // React element
  if (componentObject.$$typeof) {
    if (componentObject.type) {
      return getComponentName(componentObject.type)
    }
  }

  // String (native HTML elements)
  if (typeof component === "string") {
    return component
  }

  // Object with displayName or name
  if (component && typeof component === "object") {
    if (component.displayName) {
      return component.displayName
    }
    if (component.name) {
      return component.name
    }
    // Check if it's a React.forwardRef or React.memo wrapper
    if (component.render) {
      return getComponentName(component.render)
    }
    if (component.type) {
      return getComponentName(component.type)
    }
  }

  return "Unknown"
}

function decodeTurbopackPath(encodedPath: string): string {
  const percentEncoded = encodedPath.replace(/\$([0-9a-fA-F]{2})/g, "%$1")
  try {
    return decodeURIComponent(percentEncoded)
  } catch {
    return encodedPath
  }
}

/**
 * Extracts file path from fiber (without line numbers, since React 19 removed _debugSource)
 */
function getComponentFilePathFromFiber(
  component: ReactComponentLike,
  fiber?: ReactFiberNode
): string | null {
  if (!component) {
    return null
  }

  // First, try to get file path from fiber's debug info (most reliable)
  if (fiber) {
    // React DevTools uses _debugSource for source location
    if (fiber._debugSource?.fileName) {
      return fiber._debugSource.fileName
    }

    // Check the fiber itself for source info (sometimes stored directly)
    if (fiber._source?.fileName) {
      return fiber._source.fileName
    }

    // Sometimes it's in _debugOwner
    if (fiber._debugOwner?._debugSource?.fileName) {
      return fiber._debugOwner._debugSource.fileName
    }

    // Check elementType's __source
    const elementType = fiber.elementType as
      | ReactComponentFunction
      | ReactComponentObject
      | undefined
    if (elementType?.__source?.fileName) {
      return elementType.__source.fileName
    }

    // Check type's __source
    const fiberType = fiber.type as
      | ReactComponentFunction
      | ReactComponentObject
      | undefined
    if (fiberType?.__source?.fileName) {
      return fiberType.__source.fileName
    }
  }

  // Check if component has __source or other metadata
  const componentWithSource = component as
    | ReactComponentFunction
    | ReactComponentObject
  if (componentWithSource.__source?.fileName) {
    return componentWithSource.__source.fileName
  }

  // Check function's toString for source location hints
  if (typeof component === "function") {
    const funcString = component.toString()

    // Try to extract file path from function string (webpack/next.js sometimes includes this)
    // Look for patterns like "at ComponentName (file:///path/to/file.tsx)"
    const filePath = funcString.match(
      /\(([^)]+\.(tsx?|jsx?))(?::\d+)?(?::\d+)?\)/
    )?.[1]
    if (filePath) {
      return filePath
    }

    const turbopackFileName = funcString.match(
      /fileName:\s*"(\[project\]\/[^"]+\.(tsx?|jsx?))"/
    )?.[1]
    if (turbopackFileName) {
      return turbopackFileName
    }

    const turbopackModulePath = funcString.match(
      /__TURBOPACK__imported__module__(\$5b\$project\$5d2f[^_]+\.(tsx?|jsx?))__/
    )?.[1]
    if (turbopackModulePath) {
      return decodeTurbopackPath(turbopackModulePath)
    }

    const encodedPath = funcString.match(
      /__TURBOPACK__imported__module__([^_]+\.(tsx?|jsx?))__/
    )?.[1]
    if (encodedPath?.includes("$")) {
      return decodeTurbopackPath(encodedPath)
    }

    // Look for webpack module paths
    const webpackPath = funcString.match(
      /webpack:\/\/\/\.\/(.+\.(tsx?|jsx?))/
    )?.[1]
    if (webpackPath) {
      return webpackPath
    }

    // Look for Next.js app directory paths
    const nextAppMatch = funcString.match(/\/app\/(.+\.(tsx?|jsx?))/)
    if (nextAppMatch) {
      return `app/${nextAppMatch[1]}`
    }

    // Look for src/ paths
    const srcMatch = funcString.match(/\/src\/(.+\.(tsx?|jsx?))/)
    if (srcMatch) {
      return `src/${srcMatch[1]}`
    }

    // Try to find any file path pattern
    const path = funcString.match(
      /([a-zA-Z0-9_\-/]+\.(tsx?|jsx?))(?::\d+)?(?::\d+)?/
    )?.[1]
    // Filter out common false positives
    if (
      path &&
      !path.includes("node_modules") &&
      !path.includes("webpack") &&
      !path.includes("TURBOPACK") &&
      (path.includes("/") || path.includes("\\"))
    ) {
      return path
    }
  }

  // Check if it's wrapped (forwardRef, memo, etc.)
  if (component && typeof component === "object") {
    if (component.render) {
      return getComponentFilePathFromFiber(component.render, fiber)
    }
    if (component.type) {
      return getComponentFilePathFromFiber(component.type, fiber)
    }
  }

  return null
}

/**
 * Attempts to extract file path from component and fiber
 *
 * Note: React 19 intentionally removed _debugSource from Fiber nodes (React #28265, #29092).
 * File paths still work via component function names and __source metadata.
 */
export function getComponentFilePath(
  component: ReactComponentLike,
  fiber?: ReactFiberNode
): string | null {
  return getComponentFilePathFromFiber(component, fiber)
}

/**
 * Gets the workspace root path
 */
export function getWorkspaceRoot(): string {
  // Check if it's been set globally (can be set via window.__clickToComponentWorkspaceRoot)
  if (typeof window !== "undefined" && window.__clickToComponentWorkspaceRoot) {
    return window.__clickToComponentWorkspaceRoot
  }

  // Try to infer from the current page's meta tags or data attributes
  if (typeof document !== "undefined") {
    const metaWorkspace = document.querySelector('meta[name="workspace-root"]')
    if (metaWorkspace) {
      return metaWorkspace.getAttribute("content") || ""
    }
  }

  // Check environment variable (must be prefixed with NEXT_PUBLIC_ to be available in browser)
  if (
    typeof process !== "undefined" &&
    process.env.NEXT_PUBLIC_CLICK_TO_COMPONENT_WORKSPACE_ROOT
  ) {
    return process.env.NEXT_PUBLIC_CLICK_TO_COMPONENT_WORKSPACE_ROOT
  }

  // Return empty string if not set (will trigger instructions in UI)
  return ""
}

/**
 * Resolves a relative file path to an absolute path
 */
export function resolveFullPath(
  relativePath: string | null,
  projectRoot: string = "frontend"
): string | null {
  if (!relativePath) return null

  // Remove line/column numbers from path if present (format: path:line:column)
  let cleanPath =
    relativePath.match(/^(.+?)(?::\d+)?(?::\d+)?$/)?.[1] ?? relativePath

  // Get workspace root (needed for both absolute and relative paths)
  const workspaceRoot = getWorkspaceRoot()

  // If it's already an absolute path, check if it needs correction for @tokenterminal/core
  if (cleanPath.startsWith("/") || /^[A-Z]:/.test(cleanPath)) {
    // Check if this is an absolute path that incorrectly points to frontend/src/ui/
    // when it should be libs/ui/src/ui/ (for @tokenterminal/core imports)
    const frontendUiPath = `${workspaceRoot}/frontend/src/ui/`
    if (cleanPath.startsWith(frontendUiPath)) {
      // Convert frontend/src/ui/... to libs/ui/src/ui/...
      const relativePath = cleanPath.replace(frontendUiPath, "")
      return `${workspaceRoot}/libs/ui/src/ui/${relativePath}`
    }

    const frontendFeaturePath = `${workspaceRoot}/frontend/src/feature/`
    if (cleanPath.startsWith(frontendFeaturePath)) {
      const relativePath = cleanPath.replace(frontendFeaturePath, "")
      return `${workspaceRoot}/libs/ui/src/feature/${relativePath}`
    }

    // Check if this is an apps/landing absolute path
    const landingPath = `${workspaceRoot}/apps/landing/`
    if (cleanPath.startsWith(landingPath)) {
      return cleanPath
    }

    return cleanPath
  }

  cleanPath = cleanPath.replace(/^webpack:\/\/\/\.\//, "")
  cleanPath = cleanPath.replace(/^webpack:\/\//, "")
  cleanPath = cleanPath.replace(/^file:\/\/\//, "/")
  cleanPath = cleanPath.replace(/^file:\/\//, "")

  if (cleanPath.startsWith("[project]/")) {
    cleanPath = cleanPath.replace(/^\[project\]\//, "")
  }

  if (cleanPath.startsWith("libs/ui/")) {
    return `${workspaceRoot}/${cleanPath}`
  }

  if (cleanPath.startsWith("../libs/ui/")) {
    return `${workspaceRoot}/${cleanPath.replace(/^\.\.\//, "")}`
  }

  if (cleanPath.startsWith("web/tt-core/")) {
    return `${workspaceRoot}/${cleanPath.replace(/^web\/tt-core\//, "libs/ui/")}`
  }

  if (cleanPath.startsWith("../web/tt-core/")) {
    return `${workspaceRoot}/${cleanPath.replace(/^\.\.\/web\/tt-core\//, "libs/ui/")}`
  }

  if (cleanPath.startsWith("ui/") || cleanPath.startsWith("src/ui/")) {
    if (cleanPath.startsWith("src/ui/")) {
      return `${workspaceRoot}/libs/ui/${cleanPath}`
    }
    return `${workspaceRoot}/libs/ui/src/${cleanPath}`
  }

  if (cleanPath.includes("tt-core")) {
    return `${workspaceRoot}/${cleanPath.replace(/^(?:\.\.\/)?web\/tt-core\//, "libs/ui/")}`
  }

  // Check if path already contains "frontend/" to avoid duplication
  if (cleanPath.startsWith("frontend/")) {
    return `${workspaceRoot}/${cleanPath}`
  }

  if (cleanPath.startsWith("apps/landing/")) {
    return `${workspaceRoot}/${cleanPath}`
  }

  if (cleanPath.startsWith("src/")) {
    return `${workspaceRoot}/${projectRoot}/${cleanPath}`
  }

  if (cleanPath.startsWith("app/")) {
    return `${workspaceRoot}/${projectRoot}/${cleanPath}`
  }

  if (cleanPath.includes("node_modules")) {
    return cleanPath
  }

  if (!cleanPath.includes("/") || cleanPath.startsWith("./")) {
    return `${workspaceRoot}/${projectRoot}/${cleanPath.replace(/^\.\//, "")}`
  }

  return `${workspaceRoot}/${projectRoot}/${cleanPath}`
}

export function extractComponentInfo(
  fiber: ReactFiberNode,
  _domElement?: HTMLElement,
  projectRoot: string = "frontend"
): ComponentInfo {
  const info: ComponentInfo = {
    componentName: null,
    componentType: null,
    filePath: null,
    fullPath: null,
    props: null,
    state: null,
    hooks: [],
    hierarchy: [],
  }

  let currentFiber: ReactFiberNode | null | undefined = fiber
  let depth = 0
  const maxDepth = 50

  while (currentFiber && depth < maxDepth) {
    const elementType = currentFiber.elementType
    const type = currentFiber.type
    const memoizedProps = currentFiber.memoizedProps
    const memoizedState = currentFiber.memoizedState

    const componentType = elementType || type
    const isNativeElement = typeof componentType === "string"
    const isReactComponent =
      typeof componentType === "function" ||
      (typeof componentType === "object" &&
        componentType !== null &&
        !isNativeElement)

    if (isReactComponent) {
      const componentName = getComponentName(componentType)
      let filePath = getComponentFilePath(componentType, currentFiber)

      if (!filePath && currentFiber._debugSource?.fileName) {
        filePath = currentFiber._debugSource.fileName
      }

      if (!filePath && currentFiber._source?.fileName) {
        filePath = currentFiber._source.fileName
      }

      const fullPath = resolveFullPath(filePath, projectRoot)

      info.hierarchy.push({
        name: componentName || "Unknown",
        type: typeof componentType,
        filePath: filePath,
        fullPath: fullPath,
      })

      if (!info.componentName) {
        info.componentName = componentName
        info.componentType = typeof componentType
        info.props = memoizedProps
        info.state = memoizedState
        info.filePath = filePath
        info.fullPath = fullPath
      }
    } else if (isNativeElement && info.hierarchy.length === 0) {
      info.hierarchy.push({
        name: componentType,
        type: "native",
        filePath: null,
        fullPath: null,
      })

      if (!info.componentName) {
        info.componentName = componentType
        info.componentType = "native"
        info.filePath = null
        info.fullPath = null
      }
    }

    currentFiber = currentFiber.return
    depth++
  }

  return info
}

export function getDisplayPath(fullPath: string | null): string | null {
  if (!fullPath) return null

  const workspaceRoot = getWorkspaceRoot()

  if (fullPath.startsWith(workspaceRoot)) {
    return fullPath.replace(workspaceRoot, "").replace(/^\//, "")
  }

  return fullPath
}

/**
 * Gets the editor URL for opening a file
 */
export function getEditorUrl(filePath: string, editor: string): string | null {
  if (!filePath) {
    return null
  }

  // Map editor names to protocol handlers
  const editorProtocols: Record<string, string> = {
    cursor: "cursor://file",
    vscode: "vscode://file",
    code: "vscode://file", // VS Code alias
    webstorm: "webstorm://open",
    idea: "idea://open",
    sublime: "subl://open",
  }

  const protocol = editorProtocols[editor.toLowerCase()] || `vscode://file`

  let cleanPath = filePath
  if (cleanPath.startsWith("/")) {
    cleanPath = cleanPath.substring(1)
  }

  return `${protocol}/${cleanPath}`
}
